'use strict';

import User from 'User';

/**
 * A criteria value that has been selected by the user, with the label that was displayed
 * to him when he picked it.
 */
interface StoredCriteria {
    value: string;
    label: string;
}

/**
 * Stores on the device the criterias that the user selected in a navigator, so that they
 * survive a restart of the application : a tablet does not have to select its criteria
 * again every morning.
 *
 * The values are stored per datafield, and neither per task nor per filter : the datafield
 * IS the identity of the criteria. A selection therefore follows the user across every
 * listing whose filter declares that same datafield - navigator, dashboard tile, selection
 * popup - and stops at the ones that do not. Restricting the children of the CRM06 to a
 * group also restricts the events of the CRM40 to that group, without any additional
 * configuration, because both filters declare the criteria.
 *
 * The reading side must therefore be given the datafields declared by the filter of the
 * listing being loaded : reading everything stored would inject a criteria into a filter
 * that never declared it.
 *
 * Nothing here may throw : the storage might be unavailable (private browsing, quota, ...),
 * in which case the criterias are simply not remembered.
 */
class FilterCriteriaStore {
    private static readonly KEY_PREFIX = 'sai-criteria';

    private static buildKey(domainId: string, datafieldId: string): string {
        return FilterCriteriaStore.KEY_PREFIX + ':' + User.getId() + ':' + domainId + ':' + datafieldId;
    }

    /**
     * Stores the value selected for a criteria. An empty value removes the entry.
     * @param domainId The domain the criteria has been selected in
     * @param datafieldId The datafield of the criteria
     * @param value The value to send to the server
     * @param label The human readable value, as displayed to the user
     */
    public static store(domainId: string, datafieldId: string, value: string, label: string): void {
        try {
            let key = FilterCriteriaStore.buildKey(domainId, datafieldId);
            if(value === undefined || value === '') {
                localStorage.removeItem(key);
            } else {
                localStorage.setItem(key, JSON.stringify({ value: value, label: label }));
            }
        } catch(e) {
            console.warn('Unable to store the criteria ' + datafieldId, e);
        }
    }

    /**
     * @param domainId The domain of the request
     * @param datafieldId The datafield of the criteria
     * @returns The stored criteria, undefined if the user selected nothing
     */
    public static get(domainId: string, datafieldId: string): StoredCriteria {
        try {
            let stored = localStorage.getItem(FilterCriteriaStore.buildKey(domainId, datafieldId));
            if(stored) {
                let parsed = JSON.parse(stored);
                if(parsed.value) {
                    return { value: parsed.value, label: parsed.label ? parsed.label : parsed.value };
                }
            }
        } catch(e) {
            console.warn('Unable to read the criteria ' + datafieldId, e);
        }

        return undefined;
    }

    /**
     * Retrieves the stored value of each of the given criterias. This is what a listing
     * applies to be restricted the same way as any other listing built on a filter that
     * declares the same criterias.
     *
     * @param domainId The domain of the request
     * @param datafieldIds The datafields declared as selectable by the filter of the listing
     * @returns The values to apply, as expected by a filter context
     */
    public static getValues(domainId: string, datafieldIds: Array<string>): Array<{datafieldId: string, value: string}> {
        let values = [];
        if(domainId === undefined || datafieldIds === undefined) {
            return values;
        }

        for(let index in datafieldIds) {
            let datafieldId = datafieldIds[index];
            let stored = FilterCriteriaStore.get(domainId, datafieldId);
            if(stored !== undefined) {
                values.push({ datafieldId: datafieldId, value: stored.value });
            }
        }

        return values;
    }
}

export { FilterCriteriaStore, StoredCriteria };
